import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface WizardStepMeta {
  /** Machine key used for step routing — not displayed */
  key: string;
  /** Short label shown under the step marker on desktop */
  label: string;
}

interface WizardStepsProps {
  steps: WizardStepMeta[];
  currentStep: number;
  /** Called when the user clicks a completed step to jump back */
  onStepClick?: (index: number) => void;
}

/**
 * Numbered step indicator used at the top of the Create Card wizard.
 *
 * Each step renders as a numbered circle (checkmark once completed)
 * with its label underneath. Connector lines between circles fill in
 * as the user advances. The outer row uses `items-start` so all
 * circles align at the top and the connector line can be positioned
 * at the exact vertical centre of the circles regardless of how many
 * labels wrap.
 */
const WizardSteps = ({ steps, currentStep, onStepClick }: WizardStepsProps) => {
  return (
    <div className="w-full">
      {/* Step circles + connector lines */}
      <div className="flex items-start justify-between gap-1 sm:gap-2">
        {steps.map((step, i) => {
          const completed = i < currentStep;
          const active = i === currentStep;
          const clickable = !!onStepClick && completed;

          return (
            <div key={step.key} className="flex items-start flex-1 min-w-0 last:flex-none">
              {/* Step circle */}
              <div className="flex flex-col items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={clickable ? () => onStepClick(i) : undefined}
                  disabled={!clickable}
                  aria-current={active ? "step" : undefined}
                  className={cn(
                    "h-8 w-8 sm:h-9 sm:w-9 rounded-full border-2 flex items-center justify-center text-xs sm:text-sm font-semibold transition-all",
                    completed
                      ? "border-primary bg-primary text-primary-foreground"
                      : active
                        ? "border-primary bg-background text-primary ring-4 ring-primary/15"
                        : "border-border bg-background text-muted-foreground",
                    clickable && "cursor-pointer hover:scale-105",
                  )}
                >
                  {completed ? <Check size={16} strokeWidth={3} /> : i + 1}
                </button>
                <span
                  className={cn(
                    "text-[10px] sm:text-xs font-medium text-center max-w-[5rem] sm:max-w-[6.5rem] leading-tight",
                    active
                      ? "text-foreground"
                      : completed
                        ? "text-foreground/70"
                        : "text-muted-foreground",
                  )}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector line — centred on the circle via an
                  explicit top margin (circle half-height minus half
                  the line's 2px height). */}
              {i < steps.length - 1 && (
                <div className="flex-1 h-0.5 mx-1 sm:mx-2 mt-[15px] sm:mt-[17px] rounded-full relative bg-border overflow-hidden">
                  <div
                    className={cn(
                      "absolute inset-y-0 left-0 bg-primary transition-all duration-300",
                      completed ? "w-full" : "w-0",
                    )}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WizardSteps;
